/**
 * recordDates — reading a stored timestamp off a record, whatever shape it is in.
 *
 * The same field is written in two shapes across this database: server-written
 * rows carry epoch milliseconds (a number), while older and mobile-written rows
 * carry an ISO string. Every list that sorts by date has to cope with both.
 *
 * This exists because it did not. `listJobs` sorted with
 * `(b.createdAt || '').localeCompare(...)`, which throws on a number — so one
 * number-shaped `createdAt` failed the whole read and the projects page showed
 * "No jobs yet" to a company with three jobs. Mobile had already hit the bug and
 * fixed it in its own copy; web never got the fix, because there was no one
 * place for the rule to live.
 *
 * Keep sorting rules here rather than inline, so the next list cannot get its
 * own subtly different version.
 */

/**
 * A stored timestamp as epoch milliseconds.
 *
 *   toEpochMs(1781643383338)              → 1781643383338
 *   toEpochMs('2026-06-16T20:42:01.725Z') → 1781642521725
 *   toEpochMs(undefined)                  → 0
 *   toEpochMs('not a date')               → 0
 *
 * Returns 0 for anything unusable, so a row with no date sorts last under a
 * descending comparator instead of throwing or landing arbitrarily.
 *
 * @param {number|string|Date|null|undefined} value
 * @returns {number} epoch ms, or 0
 */
export function toEpochMs(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
}

/**
 * A record's createdAt as epoch milliseconds. Safe on null/undefined rows.
 *
 * @param {{createdAt?: number|string|Date}|null|undefined} row
 * @returns {number} epoch ms, or 0
 */
export function createdAtMs(row) {
  return toEpochMs(row?.createdAt);
}

/**
 * Comparator: newest createdAt first. Pass straight to Array.prototype.sort.
 *
 *   rows.sort(byNewestCreated)
 *
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
export function byNewestCreated(a, b) {
  return createdAtMs(b) - createdAtMs(a);
}
